import { Component, ElementRef, Renderer2, afterNextRender, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, filter, map } from 'rxjs';

import { RootPaths } from '@fcom/core/constants';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { OndMarketingRoutesPath } from '@fcom/ond-marketing-landing-core/interfaces/ond-marketing-offers.interface';

const SMARTVEL_WIDGET_DESTINATION_FIELD = 'smt-combobox-labeledby-destination';
const SMARTVEL_WIDGET_ORIGIN_FIELD = 'smt-combobox-labeledby-origin';

@Component({
  selector: 'fin-travel-restrictions-smartvel',
  templateUrl: './travel-restrictions-smartvel.component.html',
  styleUrls: ['./travel-restrictions-smartvel.component.scss'],
  standalone: false,
})
export class TravelRestrictionsSmartvelComponent {
  private languageService = inject(LanguageService);
  private route = inject(ActivatedRoute);
  private ref = inject(ElementRef);
  private renderer = inject(Renderer2);

  readonly smartvelSource = 'https://cdn.smartvel.com/scripts/gcovwidget/boot.min.js';
  readonly smartvelApiKey = '76556e09-358b-452c-bb0d-c639c16c9fc3';

  readonly smartvelWidget = viewChild.required<ElementRef<HTMLElement>>('smartvelWidget');

  readonly origin = toSignal(this.parseLocationParamFromRoute('originLocationCode'));
  readonly destination = toSignal(this.parseLocationParamFromRoute('destinationLocationCode'));
  readonly oneway = toSignal(
    this.route.queryParamMap.pipe(
      map((params: ParamMap) => {
        return params.get('oneway') === 'true';
      })
    )
  );

  private readonly originLocationCode = signal<string | null>(null);
  private readonly destinationLocationCode = signal<string | null>(null);

  readonly flightsPageRedirectionUrl = computed(() => {
    const originLocationCode = this.originLocationCode();
    const destinationLocationCode = this.destinationLocationCode();
    return originLocationCode && destinationLocationCode
      ? this.createRedirectionUrl(originLocationCode, destinationLocationCode)
      : null;
  });

  constructor() {
    afterNextRender(() => {
      this.loadScript(this.smartvelSource);
    });
  }

  onWidgetChange(record: MutationRecord): void {
    if (record.attributeName == 'data-value') {
      const id = (record.target as Element)?.id;
      if (id === SMARTVEL_WIDGET_DESTINATION_FIELD) {
        this.destinationLocationCode.set(this.parseLocCodeFromSmartvelWidget(SMARTVEL_WIDGET_DESTINATION_FIELD));
      }
      if (id === SMARTVEL_WIDGET_ORIGIN_FIELD) {
        this.originLocationCode.set(this.parseLocCodeFromSmartvelWidget(SMARTVEL_WIDGET_ORIGIN_FIELD));
      }
    }
  }

  private parseLocationParamFromRoute(param: string): Observable<string> {
    return this.route.queryParamMap.pipe(
      map((params: ParamMap) => {
        return params.get(param);
      }),
      filter((iataCode) => {
        return !!iataCode && iataCode.length === 3;
      })
    );
  }

  private createRedirectionUrl(originLocationCode: string, destinationLocationCode: string): string {
    return `${this.languageService.langValue}/${RootPaths.OND_MARKETING_LANDING_ROOT}/${OndMarketingRoutesPath.CITY_TO_CITY}/${originLocationCode}/${destinationLocationCode}`;
  }

  private parseLocCodeFromSmartvelWidget(selector: string): string | null {
    const location = this.smartvelWidget().nativeElement.querySelector<HTMLInputElement>(`#${selector}`)?.value;
    // Value is like 'Berlin, Germany (BER)' or 'New York, United States of America (USA) (JFK)' or 'Everywhere' when defined
    if (!location || location === 'Everywhere') {
      return null;
    }
    return [...location.matchAll(/\(([A-Z]+)+\)/g)].pop()[1];
  }

  private loadScript(src: string): void {
    const script: HTMLScriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setAttribute(script, 'src', src);
    this.renderer.appendChild(this.ref.nativeElement, script);
  }
}
